import React, { useState, useEffect } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DOMPurify from "dompurify";
import DropZone from "./DropZone";
import { Editor } from "@tinymce/tinymce-react";
import {
  GripVertical, Trash2, Image as ImageIcon, Youtube, Layout, ListChecks,
  Quote, Music, FileText, FileDown, Plus, FormInput, MessageSquare,
  Star, Grid, Code2, Minus, Video, Palette, HelpCircle, GitBranch,
  LayoutGrid, Building2, Briefcase, Loader2,
} from "lucide-react";
import { api } from "../lib/api";

/**
 * LPBlocksEditor — editor drag-and-drop para blocos de Landing Pages.
 *
 * Tipos suportados (13):
 *   - hero:       { image, video_url, overlay_opacity, title, subtitle, cta_label, cta_link, height }
 *   - richtext:   { html, max_width, align, bg }
 *   - image:      { src, alt, caption, max_width }
 *   - gallery:    { images:[{src,alt,caption}], columns }
 *   - video:      { url, caption }                    (YouTube/Vimeo/embed)
 *   - features:   { title, subtitle, columns, items:[{icon,title,description}] }
 *   - testimonial:{ items:[{quote,author,role,avatar}] }
 *   - form:       { form_id, title, subtitle, fields:[...], submit_label, thank_you }
 *   - cta:        { title, subtitle, buttons:[{label,link,style,is_whatsapp,whatsapp_phone}] }
 *   - spacer:     { height_px, bg }
 *   - divider:    { style, color }                    (line divider)
 *   - html:       { html }                            (raw HTML — sanitized)
 *   - stats:      { items:[{value,label,suffix}], columns }
 *
 * Todos os blocos têm `bg` opcional (background color), `spacing_px` (padding
 * vertical acima), e `id` único.
 */

export function newLPBlockId() {
  return `lb_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

export function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

const TYPE_META = {
  hero:        { label: "Hero (imagem + título)",     Icon: Layout },
  richtext:    { label: "Texto formatado",             Icon: FileText },
  image:       { label: "Imagem",                      Icon: ImageIcon },
  gallery:     { label: "Galeria",                     Icon: Grid },
  video:       { label: "Vídeo (YouTube/Vimeo)",       Icon: Video },
  features:    { label: "Grid de benefícios",          Icon: ListChecks },
  testimonial: { label: "Depoimento",                  Icon: Quote },
  form:        { label: "Formulário",                  Icon: FormInput },
  cta:         { label: "CTA + botões (WhatsApp)",     Icon: MessageSquare },
  spacer:      { label: "Espaço em branco",            Icon: Minus },
  divider:     { label: "Divisor",                     Icon: Minus },
  html:        { label: "HTML customizado",            Icon: Code2 },
  stats:       { label: "Números / stats",             Icon: Star },
  multi_step_form:  { label: "Formulário em etapas",   Icon: FormInput },
  logos_band:       { label: "Faixa de logos",         Icon: LayoutGrid },
  faq:              { label: "FAQ (acordeão)",         Icon: HelpCircle },
  process_timeline: { label: "Processo (etapas)",       Icon: GitBranch },
  services_grouped: { label: "Serviços agrupados",      Icon: Briefcase },
  case_showcase:    { label: "Cases do site",           Icon: Building2 },
};

/* -------------------- Sortable wrapper -------------------- */
function SortableItem({ block, index, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const meta = TYPE_META[block.type] || {};
  const Icon = meta.Icon || FileText;
  const upd = (patch) => onChange({ ...block, ...patch });
  const [showStyle, setShowStyle] = useState(false);
  const hasStyle = !!(block.bg_color || block.title_color || block.body_color || block.accent_color || block.btn_bg || block.min_height || block.pad_bottom);

  return (
    <div ref={setNodeRef} style={style} className="border border-black/10 rounded-md bg-white overflow-hidden" data-testid={`lpblock-${block.type}-${index}`}>
      <header className="flex items-center gap-2 px-3 py-2 bg-neutral-50 border-b border-black/10">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-black" title="Arrastar para reordenar">
          <GripVertical className="w-4 h-4" />
        </button>
        <Icon className="w-4 h-4 text-accent-orange" />
        <span className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{meta.label || block.type}</span>
        <span className="text-[10px] font-mono text-neutral-400">#{index + 1}</span>
        <div className="ml-auto flex items-center gap-3">
          <button onClick={() => setShowStyle((v) => !v)}
            data-testid={`lpblock-style-toggle-${index}`}
            className={`text-[10px] uppercase font-semibold tracking-widest px-2.5 py-1 rounded-full flex items-center gap-1 ${hasStyle ? "bg-black text-white" : "border border-black/20 text-neutral-600"}`}
            title="Cores, espaçamento e altura desta dobra">
            <Palette className="w-3 h-3" /> Estilo {hasStyle && "•"}
          </button>
          <button onClick={() => onRemove(block.id)} className="text-red-500 hover:text-red-700" title="Remover">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </header>
      {showStyle && <BlockStylePanel block={block} upd={upd} index={index} />}
      <div className="p-3">
        {block.type === "hero"        && <HeroEditor block={block} upd={upd} index={index} />}
        {block.type === "richtext"    && <RichTextBlockEditor block={block} upd={upd} index={index} />}
        {block.type === "image"       && <ImageBlockEditor block={block} upd={upd} index={index} />}
        {block.type === "gallery"     && <GalleryEditor block={block} upd={upd} index={index} />}
        {block.type === "video"       && <VideoEditor block={block} upd={upd} index={index} />}
        {block.type === "features"    && <FeaturesEditor block={block} upd={upd} index={index} />}
        {block.type === "testimonial" && <TestimonialEditor block={block} upd={upd} index={index} />}
        {block.type === "form"        && <FormBlockEditor block={block} upd={upd} index={index} />}
        {block.type === "cta"         && <CtaEditor block={block} upd={upd} index={index} />}
        {block.type === "spacer"      && <SpacerEditor block={block} upd={upd} index={index} />}
        {block.type === "divider"     && <DividerEditor block={block} upd={upd} index={index} />}
        {block.type === "html"        && <HtmlEditor block={block} upd={upd} index={index} />}
        {block.type === "stats"       && <StatsEditor block={block} upd={upd} index={index} />}
        {block.type === "multi_step_form"  && <MultiStepFormEditor block={block} upd={upd} index={index} />}
        {block.type === "logos_band"       && <LogosBandEditor block={block} upd={upd} index={index} />}
        {block.type === "faq"              && <FaqEditor block={block} upd={upd} index={index} />}
        {block.type === "process_timeline" && <ProcessTimelineEditor block={block} upd={upd} index={index} />}
        {block.type === "services_grouped" && <ServicesGroupedEditor block={block} upd={upd} index={index} />}
        {block.type === "case_showcase"    && <CaseShowcaseEditor block={block} upd={upd} index={index} />}
      </div>
    </div>
  );
}

/* -------------------- Estilo da dobra (cores + espaçamento + altura) -------------------- */
/** Um único painel reaproveitado por TODOS os tipos de bloco. Grava:
 *   bg_color / title_color / body_color / accent_color / btn_bg / btn_text
 *   pad_top / pad_bottom / min_height
 *  Campos vazios = herda o design padrão do template. */
function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-8 h-8 border border-black/15 rounded cursor-pointer bg-white p-0.5" />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="padrão"
          className="flex-1 min-w-0 border border-black/15 px-2 py-1.5 rounded text-[11px] font-mono" />
        {value && <button onClick={() => onChange("")} title="Limpar" className="text-neutral-400 hover:text-red-500 text-xs px-1">×</button>}
      </div>
    </div>
  );
}

function BlockStylePanel({ block, upd, index }) {
  const padTop = block.pad_top ?? block.spacing_px ?? 48;
  return (
    <div className="px-3 py-3 bg-neutral-100/70 border-b border-black/10 space-y-3" data-testid={`lpblock-style-panel-${index}`}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <ColorField label="Fundo da dobra" value={block.bg_color} onChange={(v) => upd({ bg_color: v })} testid={`lpblock-bgcolor-${index}`} />
        <ColorField label="Títulos" value={block.title_color} onChange={(v) => upd({ title_color: v })} testid={`lpblock-titlecolor-${index}`} />
        <ColorField label="Textos" value={block.body_color} onChange={(v) => upd({ body_color: v })} testid={`lpblock-bodycolor-${index}`} />
        <ColorField label="Destaque / ícones" value={block.accent_color} onChange={(v) => upd({ accent_color: v })} testid={`lpblock-accentcolor-${index}`} />
        <ColorField label="Botão — fundo" value={block.btn_bg} onChange={(v) => upd({ btn_bg: v })} testid={`lpblock-btnbg-${index}`} />
        <ColorField label="Botão — texto" value={block.btn_text} onChange={(v) => upd({ btn_text: v })} testid={`lpblock-btntext-${index}`} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 flex justify-between">
            Espaço acima <span className="font-mono text-black">{padTop}px</span>
          </label>
          <input type="range" min={0} max={240} step={4} value={padTop}
            onChange={(e) => upd({ pad_top: Number(e.target.value), spacing_px: Number(e.target.value) })}
            data-testid={`lpblock-padtop-${index}`} className="w-full" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 flex justify-between">
            Espaço abaixo <span className="font-mono text-black">{block.pad_bottom ?? 0}px</span>
          </label>
          <input type="range" min={0} max={240} step={4} value={block.pad_bottom ?? 0}
            onChange={(e) => upd({ pad_bottom: Number(e.target.value) })}
            data-testid={`lpblock-padbottom-${index}`} className="w-full" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 flex justify-between">
            Altura mínima <span className="font-mono text-black">{block.min_height ? `${block.min_height}px` : "auto"}</span>
          </label>
          <input type="range" min={0} max={900} step={20} value={block.min_height ?? 0}
            onChange={(e) => upd({ min_height: Number(e.target.value) })}
            data-testid={`lpblock-minheight-${index}`} className="w-full" />
        </div>
      </div>
      <p className="text-[10px] text-neutral-400">
        Campos de cor vazios herdam o design padrão do template. <strong>Altura mínima 0 = automática</strong> (a dobra ocupa só o necessário).
        Use <em>espaço acima/abaixo</em> para aproximar dobras e calibrar o ritmo da página.
      </p>
    </div>
  );
}

/* -------------------- Block-type editors -------------------- */
function HeroEditor({ block, upd, index }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="space-y-2">
        <label className="text-xs text-neutral-500">Eyebrow (linha pequena acima do título)</label>
        <input value={block.eyebrow || ""} onChange={(e) => upd({ eyebrow: e.target.value })}
          data-testid={`lpblock-hero-eyebrow-${index}`}
          placeholder="Ex: Marketing imobiliário desde 1991"
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
        <label className="text-xs text-neutral-500">Título principal (H1)</label>
        <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })}
          data-testid={`lpblock-hero-title-${index}`}
          placeholder="Título de impacto em duas linhas"
          className="w-full border border-black/15 px-3 py-2 rounded text-sm font-display text-lg" />
        <label className="text-xs text-neutral-500">Subtítulo</label>
        <textarea value={block.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })}
          data-testid={`lpblock-hero-subtitle-${index}`}
          placeholder="Explica o principal benefício em 1-2 frases."
          rows={3}
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-neutral-500">CTA — texto</label>
            <input value={block.cta_label || ""} onChange={(e) => upd({ cta_label: e.target.value })}
              placeholder="Falar com especialista"
              className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
          </div>
          <div>
            <label className="text-xs text-neutral-500">CTA — link (ou #form)</label>
            <input value={block.cta_link || ""} onChange={(e) => upd({ cta_link: e.target.value })}
              placeholder="#form ou https://..."
              className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-neutral-500">CTA secundário — texto</label>
            <input value={block.cta_secondary_label || ""} onChange={(e) => upd({ cta_secondary_label: e.target.value })}
              placeholder="Ver cases" className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
          </div>
          <div>
            <label className="text-xs text-neutral-500">CTA secundário — link</label>
            <input value={block.cta_secondary_link || ""} onChange={(e) => upd({ cta_secondary_link: e.target.value })}
              placeholder="#cases" className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
          </div>
        </div>
        <div>
          <label className="text-xs text-neutral-500">Microprova (frase pequena no rodapé do hero)</label>
          <textarea rows={2} value={block.microproof || ""} onChange={(e) => upd({ microproof: e.target.value })}
            placeholder="Ex: +300 empreendimentos lançados em 12 estados."
            className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-neutral-500">Altura</label>
            <select value={block.height || "70vh"} onChange={(e) => upd({ height: e.target.value })}
              className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
              <option value="60vh">Curta (60vh)</option>
              <option value="70vh">Média (70vh)</option>
              <option value="90vh">Alta (90vh)</option>
              <option value="100vh">Tela cheia (100vh)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-neutral-500">Overlay {Math.round((block.overlay_opacity ?? 0.4) * 100)}%</label>
            <input type="range" min={0} max={1} step={0.05} value={block.overlay_opacity ?? 0.4}
              onChange={(e) => upd({ overlay_opacity: Number(e.target.value) })}
              className="w-full" />
          </div>
        </div>
        <div>
          <label className="text-xs text-neutral-500">Texto — cor</label>
          <select value={block.text_color || "white"} onChange={(e) => upd({ text_color: e.target.value })}
            className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
            <option value="white">Branco (fundo escuro)</option>
            <option value="black">Preto (fundo claro)</option>
          </select>
        </div>
        <div className="border-t border-black/10 pt-3 mt-2">
          <label className="text-sm flex items-center gap-2" data-testid={`lpblock-hero-google-partner-${index}`}>
            <input type="checkbox" checked={!!block.show_google_partner} onChange={(e) => upd({ show_google_partner: e.target.checked })} />
            <span>Exibir selo <strong>Google Partner</strong> no Hero</span>
          </label>
          {block.show_google_partner && (
            <div className="mt-2 space-y-2">
              <input value={block.google_partner_label || ""} onChange={(e) => upd({ google_partner_label: e.target.value })}
                placeholder="Google Partner (texto do selo)"
                className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
              {block.google_partner_image && <img src={resolveSrc(block.google_partner_image)} alt="" className="h-12 object-contain bg-neutral-100 rounded p-1" />}
              <DropZone multiple={false} accept={{ "image/*": [] }} hint="Selo oficial (PNG/SVG) — opcional, substitui o selo gerado em código"
                onUploaded={(f) => upd({ google_partner_image: f.url })} />
              {block.google_partner_image && (
                <button onClick={() => upd({ google_partner_image: "" })} className="text-[11px] text-red-500">Remover imagem e voltar ao selo em código</button>
              )}
            </div>
          )}
        </div>
        <div className="border-t border-black/10 pt-3 mt-2">
          <label className="text-sm flex items-center gap-2" data-testid={`lpblock-hero-show-brand-${index}`}>
            <input type="checkbox" checked={!!block.show_brand} onChange={(e) => upd({ show_brand: e.target.checked })} />
            <span>Exibir marca <strong>places</strong> no canto superior esquerdo do Hero (alinhada ao conteúdo)</span>
          </label>
          {block.show_brand && (
            <div className="mt-2">
              <label className="text-xs text-neutral-500">Tipografia da marca</label>
              <select value={block.brand_color || "white"} onChange={(e) => upd({ brand_color: e.target.value })}
                data-testid={`lpblock-hero-brand-color-${index}`}
                className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
                <option value="white">Branca (para imagens escuras)</option>
                <option value="black">Preta (para imagens claras)</option>
              </select>
              <p className="text-[10px] text-neutral-400 mt-1">O símbolo hexagonal ao lado da palavra continua com a animação padrão da marca (laranja / verde / amarelo). <strong>Dica:</strong> se você ativou a <em>marca no topo da página</em> nas configurações da LP, este brand do hero é ocultado automaticamente pra evitar sobreposição.</p>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-xs font-semibold text-neutral-700">Imagem de fundo do Hero</label>
        {block.image && <img src={resolveSrc(block.image)} alt="" className="w-full aspect-video object-cover rounded border border-black/10" />}
        <input value={block.image || ""} onChange={(e) => upd({ image: e.target.value })}
          placeholder="URL da imagem" className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
        <DropZone multiple={false} accept={{ "image/*": [] }} hint="JPG/PNG/WEBP · até 25MB"
          onUploaded={(f) => upd({ image: f.url })} />
        <label className="text-xs text-neutral-500 mt-2">Ou URL de vídeo em bg (mp4)</label>
        <input value={block.video_url || ""} onChange={(e) => upd({ video_url: e.target.value })}
          placeholder="https://..." className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
      </div>
    </div>
  );
}

function RichTextBlockEditor({ block, upd, index }) {
  return (
    <div className="space-y-2">
      <Editor
        apiKey="no-api-key"
        value={block.html || ""}
        onEditorChange={(html) => upd({ html })}
        init={{
          height: 300, menubar: false, license_key: "gpl",
          plugins: "link lists autolink table",
          toolbar: "undo redo | h2 h3 | bold italic underline | bullist numlist | link | table",
        }}
      />
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-xs text-neutral-500">Largura máx</label>
          <select value={block.max_width || "prose"} onChange={(e) => upd({ max_width: e.target.value })}
            className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
            <option value="prose">Coluna leitura (max-w-3xl)</option>
            <option value="wide">Largo (max-w-5xl)</option>
            <option value="full">Total</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-500">Alinhamento</label>
          <select value={block.align || "left"} onChange={(e) => upd({ align: e.target.value })}
            className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
            <option value="left">Esquerda</option>
            <option value="center">Centro</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-500">Fundo</label>
          <select value={block.bg || "white"} onChange={(e) => upd({ bg: e.target.value })}
            className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
            <option value="white">Branco</option>
            <option value="neutral">Cinza claro</option>
            <option value="black">Preto</option>
            <option value="orange">Laranja</option>
            <option value="yellow">Amarelo</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function ImageBlockEditor({ block, upd, index }) {
  return (
    <div className="space-y-2">
      {block.src && <img src={resolveSrc(block.src)} alt={block.alt || ""} className="w-full max-h-72 object-contain border border-black/10 rounded" />}
      <input value={block.src || ""} onChange={(e) => upd({ src: e.target.value })}
        placeholder="URL da imagem"
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
      <DropZone multiple={false} accept={{ "image/*": [] }} onUploaded={(f) => upd({ src: f.url })} />
      <div className="grid grid-cols-2 gap-2">
        <input value={block.alt || ""} onChange={(e) => upd({ alt: e.target.value })}
          placeholder="ALT (SEO)"
          className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
        <input value={block.caption || ""} onChange={(e) => upd({ caption: e.target.value })}
          placeholder="Legenda (opcional)"
          className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
      </div>
      <select value={block.max_width || "wide"} onChange={(e) => upd({ max_width: e.target.value })}
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
        <option value="prose">Coluna leitura</option>
        <option value="wide">Largo</option>
        <option value="full">Full-bleed</option>
      </select>
    </div>
  );
}

function GalleryEditor({ block, upd, index }) {
  const images = block.images || [];
  const set = (i, patch) => upd({ images: images.map((x, k) => k === i ? { ...x, ...patch } : x) });
  const remove = (i) => upd({ images: images.filter((_, k) => k !== i) });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
        {images.map((img, i) => (
          <div key={i} className="border border-black/10 p-2 rounded space-y-1">
            {img.src && <img src={resolveSrc(img.src)} alt="" className="w-full aspect-square object-cover rounded" />}
            <input value={img.src || ""} onChange={(e) => set(i, { src: e.target.value })}
              placeholder="URL" className="w-full border border-black/15 px-1.5 py-1 rounded text-[10px] font-mono" />
            <input value={img.caption || ""} onChange={(e) => set(i, { caption: e.target.value })}
              placeholder="Legenda" className="w-full border border-black/15 px-1.5 py-1 rounded text-[10px]" />
            <button onClick={() => remove(i)} className="text-[10px] text-red-500">Remover</button>
          </div>
        ))}
      </div>
      <DropZone multiple onUploaded={(f) => upd({ images: [...images, { src: f.url, alt: "", caption: "" }] })} />
      <div>
        <label className="text-xs text-neutral-500">Colunas por linha</label>
        <select value={block.columns || 3} onChange={(e) => upd({ columns: Number(e.target.value) })}
          className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
          {[2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );
}

function VideoEditor({ block, upd, index }) {
  return (
    <div className="space-y-2">
      <input value={block.url || ""} onChange={(e) => upd({ url: e.target.value })}
        placeholder="URL YouTube, Vimeo ou MP4"
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
      <input value={block.caption || ""} onChange={(e) => upd({ caption: e.target.value })}
        placeholder="Legenda"
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
    </div>
  );
}

function FeaturesEditor({ block, upd, index }) {
  const items = block.items || [];
  const set = (i, patch) => upd({ items: items.map((x, k) => k === i ? { ...x, ...patch } : x) });
  const remove = (i) => upd({ items: items.filter((_, k) => k !== i) });
  const add = () => upd({ items: [...items, { icon: "star", title: "", description: "" }] });
  return (
    <div className="space-y-2">
      <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })}
        placeholder="Título da seção"
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-display text-lg" />
      <textarea value={block.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })}
        placeholder="Subtítulo (opcional)"
        rows={2}
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
      <div>
        <label className="text-xs text-neutral-500">Colunas</label>
        <select value={block.columns || 3} onChange={(e) => upd({ columns: Number(e.target.value) })}
          className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
          {[2,3,4,6].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="border border-black/10 p-2 rounded">
            <div className="grid grid-cols-4 gap-2">
              <input value={it.icon || ""} onChange={(e) => set(i, { icon: e.target.value })}
                placeholder="Ícone (lucide, ex: star)"
                className="border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
              <input value={it.title || ""} onChange={(e) => set(i, { title: e.target.value })}
                placeholder="Título curto"
                className="border border-black/15 px-2 py-1.5 rounded text-sm col-span-3" />
            </div>
            <textarea value={it.description || ""} onChange={(e) => set(i, { description: e.target.value })}
              placeholder="Descrição breve"
              rows={2}
              className="w-full border border-black/15 px-2 py-1.5 rounded text-sm mt-2" />
            <button onClick={() => remove(i)} className="text-xs text-red-500 mt-1">Remover</button>
          </div>
        ))}
      </div>
      <button onClick={add} className="text-xs bg-accent-yellow text-black px-3 py-1.5 rounded-full font-semibold">
        <Plus className="w-3 h-3 inline mr-1" /> Adicionar benefício
      </button>
    </div>
  );
}

function TestimonialEditor({ block, upd, index }) {
  const items = block.items || [];
  const set = (i, patch) => upd({ items: items.map((x, k) => k === i ? { ...x, ...patch } : x) });
  const remove = (i) => upd({ items: items.filter((_, k) => k !== i) });
  const add = () => upd({ items: [...items, { quote: "", author: "", role: "", avatar: "" }] });
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="border border-black/10 p-2 rounded space-y-2">
          <textarea value={it.quote || ""} onChange={(e) => set(i, { quote: e.target.value })}
            placeholder="Depoimento (aspas serão adicionadas)"
            rows={3}
            className="w-full border border-black/15 px-2 py-1.5 rounded text-sm italic" />
          <div className="grid grid-cols-3 gap-2">
            <input value={it.author || ""} onChange={(e) => set(i, { author: e.target.value })}
              placeholder="Nome" className="border border-black/15 px-2 py-1.5 rounded text-sm" />
            <input value={it.role || ""} onChange={(e) => set(i, { role: e.target.value })}
              placeholder="Cargo, Empresa" className="border border-black/15 px-2 py-1.5 rounded text-sm" />
            <input value={it.avatar || ""} onChange={(e) => set(i, { avatar: e.target.value })}
              placeholder="URL avatar" className="border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
          </div>
          <DropZone multiple={false} accept={{ "image/*": [] }} onUploaded={(f) => set(i, { avatar: f.url })} />
          <button onClick={() => remove(i)} className="text-xs text-red-500">Remover</button>
        </div>
      ))}
      <button onClick={add} className="text-xs bg-accent-yellow text-black px-3 py-1.5 rounded-full font-semibold">
        <Plus className="w-3 h-3 inline mr-1" /> Adicionar depoimento
      </button>
    </div>
  );
}

function FormBlockEditor({ block, upd, index }) {
  const fields = block.fields || [];
  const set = (i, patch) => upd({ fields: fields.map((x, k) => k === i ? { ...x, ...patch } : x) });
  const remove = (i) => upd({ fields: fields.filter((_, k) => k !== i) });
  const add = () => upd({ fields: [...fields, { name: `campo_${fields.length + 1}`, label: "", type: "text", required: false, placeholder: "" }] });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input value={block.form_id || ""} onChange={(e) => upd({ form_id: e.target.value })}
          placeholder="ID único do formulário (ex: form-hero)"
          className="border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
        <input value={block.submit_label || ""} onChange={(e) => upd({ submit_label: e.target.value })}
          placeholder="Texto do botão (ex: Falar com especialista)"
          className="border border-black/15 px-2 py-1.5 rounded text-sm" />
      </div>
      <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })}
        placeholder="Título do formulário"
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-display text-base" />
      <textarea value={block.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })}
        placeholder="Subtítulo (opcional)"
        rows={2}
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
      <input value={block.thank_you || ""} onChange={(e) => upd({ thank_you: e.target.value })}
        placeholder="Mensagem após envio (ex: Obrigado! Falaremos em breve.)"
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
      <div className="border-t border-black/10 pt-2">
        <div className="text-xs uppercase font-semibold text-neutral-500 mb-2">Campos do formulário</div>
        {fields.map((f, i) => (
          <div key={i} className="border border-black/10 p-2 rounded mb-2 grid grid-cols-6 gap-2">
            <input value={f.name || ""} onChange={(e) => set(i, { name: e.target.value })}
              placeholder="Nome interno (ex: email)" className="border border-black/15 px-2 py-1 rounded text-xs font-mono col-span-2" />
            <input value={f.label || ""} onChange={(e) => set(i, { label: e.target.value })}
              placeholder="Label visível" className="border border-black/15 px-2 py-1 rounded text-xs col-span-2" />
            <select value={f.type || "text"} onChange={(e) => set(i, { type: e.target.value })}
              className="border border-black/15 px-2 py-1 rounded text-xs">
              <option value="text">Texto</option>
              <option value="email">Email</option>
              <option value="tel">Telefone</option>
              <option value="textarea">Área de texto</option>
              <option value="select">Seleção</option>
              <option value="checkbox">Checkbox</option>
            </select>
            <label className="text-xs flex items-center gap-1">
              <input type="checkbox" checked={f.required || false} onChange={(e) => set(i, { required: e.target.checked })} />
              Obrig.
            </label>
            <input value={f.placeholder || ""} onChange={(e) => set(i, { placeholder: e.target.value })}
              placeholder="Placeholder" className="border border-black/15 px-2 py-1 rounded text-xs col-span-5" />
            {f.type === "select" && (
              <input value={(f.options || []).join(", ")} onChange={(e) => set(i, { options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                placeholder="Opções separadas por vírgula"
                className="border border-black/15 px-2 py-1 rounded text-xs col-span-6" />
            )}
            <button onClick={() => remove(i)} className="text-xs text-red-500 col-span-6 text-right">Remover campo</button>
          </div>
        ))}
        <button onClick={add} className="text-xs bg-accent-yellow text-black px-3 py-1.5 rounded-full font-semibold">
          <Plus className="w-3 h-3 inline mr-1" /> Novo campo
        </button>
      </div>
    </div>
  );
}

function CtaEditor({ block, upd, index }) {
  const btns = block.buttons || [];
  const set = (i, patch) => upd({ buttons: btns.map((x, k) => k === i ? { ...x, ...patch } : x) });
  const remove = (i) => upd({ buttons: btns.filter((_, k) => k !== i) });
  const add = () => upd({ buttons: [...btns, { label: "", link: "", style: "primary", is_whatsapp: false, whatsapp_phone: "" }] });
  return (
    <div className="space-y-2">
      <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })}
        placeholder="Título de CTA"
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-display text-lg" />
      <textarea value={block.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })}
        placeholder="Subtítulo (opcional)"
        rows={2}
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
      <div className="space-y-2">
        {btns.map((b, i) => (
          <div key={i} className="border border-black/10 p-2 rounded space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <input value={b.label || ""} onChange={(e) => set(i, { label: e.target.value })}
                placeholder="Texto do botão" className="border border-black/15 px-2 py-1.5 rounded text-sm col-span-2" />
              <select value={b.style || "primary"} onChange={(e) => set(i, { style: e.target.value })}
                className="border border-black/15 px-2 py-1.5 rounded text-sm">
                <option value="primary">Preto sólido</option>
                <option value="yellow">Amarelo</option>
                <option value="orange">Laranja</option>
                <option value="whatsapp">Verde WhatsApp</option>
                <option value="outline">Contorno</option>
              </select>
            </div>
            <label className="text-xs flex items-center gap-2">
              <input type="checkbox" checked={b.is_whatsapp || false} onChange={(e) => set(i, { is_whatsapp: e.target.checked })} />
              É botão de WhatsApp (abre wa.me com telefone)
            </label>
            {b.is_whatsapp ? (
              <input value={b.whatsapp_phone || ""} onChange={(e) => set(i, { whatsapp_phone: e.target.value })}
                placeholder="Telefone (só números, com DDI. Ex: 5562999999999)"
                className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
            ) : (
              <input value={b.link || ""} onChange={(e) => set(i, { link: e.target.value })}
                placeholder="Link (URL ou #anchor)"
                className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
            )}
            <input value={b.whatsapp_message || ""} onChange={(e) => set(i, { whatsapp_message: e.target.value })}
              placeholder="Mensagem inicial do WhatsApp (opcional)"
              className={`w-full border border-black/15 px-2 py-1.5 rounded text-sm ${b.is_whatsapp ? "" : "opacity-50"}`}
              disabled={!b.is_whatsapp} />
            <button onClick={() => remove(i)} className="text-xs text-red-500">Remover</button>
          </div>
        ))}
      </div>
      <button onClick={add} className="text-xs bg-accent-yellow text-black px-3 py-1.5 rounded-full font-semibold">
        <Plus className="w-3 h-3 inline mr-1" /> Adicionar botão
      </button>
    </div>
  );
}

function SpacerEditor({ block, upd }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-neutral-500">Altura {block.height_px || 60}px</label>
        <input type="range" min={0} max={300} step={10} value={block.height_px || 60}
          onChange={(e) => upd({ height_px: Number(e.target.value) })}
          className="w-full" />
      </div>
      <div>
        <label className="text-xs text-neutral-500">Fundo</label>
        <select value={block.bg || "white"} onChange={(e) => upd({ bg: e.target.value })}
          className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
          <option value="white">Branco</option>
          <option value="neutral">Cinza</option>
          <option value="black">Preto</option>
        </select>
      </div>
    </div>
  );
}

function DividerEditor({ block, upd }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <select value={block.style || "line"} onChange={(e) => upd({ style: e.target.value })}
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
        <option value="line">Linha fina</option>
        <option value="dashed">Tracejada</option>
        <option value="thick">Grossa</option>
      </select>
      <input value={block.color || ""} onChange={(e) => upd({ color: e.target.value })}
        placeholder="Cor (hex, ex: #ff5100 ou vazio p/ preto)"
        className="border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
    </div>
  );
}

function HtmlEditor({ block, upd, index }) {
  return (
    <div>
      <textarea value={block.html || ""} onChange={(e) => upd({ html: e.target.value })}
        placeholder="<div>Seu HTML sanitizado no render. Não injete scripts.</div>"
        rows={8}
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
      <div className="text-[10px] text-neutral-400 mt-1">HTML é sanitizado no render (DOMPurify) — tags &lt;script&gt; e handlers inline serão removidos.</div>
    </div>
  );
}

function StatsEditor({ block, upd, index }) {
  const items = block.items || [];
  const set = (i, patch) => upd({ items: items.map((x, k) => k === i ? { ...x, ...patch } : x) });
  const remove = (i) => upd({ items: items.filter((_, k) => k !== i) });
  const add = () => upd({ items: [...items, { value: "", label: "", suffix: "" }] });
  return (
    <div className="space-y-2">
      <select value={block.columns || 3} onChange={(e) => upd({ columns: Number(e.target.value) })}
        className="w-full border border-black/15 px-2 py-1.5 rounded text-sm">
        {[2,3,4,6].map(n => <option key={n} value={n}>{n} colunas</option>)}
      </select>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-4 gap-2 border border-black/10 p-2 rounded">
          <input value={it.value || ""} onChange={(e) => set(i, { value: e.target.value })}
            placeholder="Valor (ex: 300+)" className="border border-black/15 px-2 py-1 rounded text-sm font-display" />
          <input value={it.suffix || ""} onChange={(e) => set(i, { suffix: e.target.value })}
            placeholder="Sufixo (ex: k, %)" className="border border-black/15 px-2 py-1 rounded text-sm" />
          <input value={it.label || ""} onChange={(e) => set(i, { label: e.target.value })}
            placeholder="Rótulo (ex: empreendimentos)" className="border border-black/15 px-2 py-1 rounded text-sm col-span-2" />
          <button onClick={() => remove(i)} className="text-xs text-red-500 col-span-4 text-right">Remover</button>
        </div>
      ))}
      <button onClick={add} className="text-xs bg-accent-yellow text-black px-3 py-1.5 rounded-full font-semibold">
        <Plus className="w-3 h-3 inline mr-1" /> Adicionar stat
      </button>
    </div>
  );
}

/* -------------------- NEW: editores dos blocos do template Premium -------------------- */
/** Todos os 6 tipos abaixo eram renderizados na LP pública mas NÃO tinham
 *  editor no CMS — ou seja, o conteúdo vinha travado do template. Agora são
 *  100% editáveis. */

const IN = "w-full border border-black/15 px-2 py-1.5 rounded text-sm";
const TA = `${IN} leading-relaxed`;
const ADD_BTN = "text-xs bg-accent-yellow text-black px-3 py-1.5 rounded-full font-semibold";

function useListOps(list, key, upd) {
  const set = (i, patch) => upd({ [key]: list.map((x, k) => (k === i ? { ...x, ...patch } : x)) });
  const remove = (i) => upd({ [key]: list.filter((_, k) => k !== i) });
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const copy = [...list];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    upd({ [key]: copy });
  };
  return { set, remove, move };
}

function RowTools({ i, len, move, remove }) {
  return (
    <div className="flex items-center gap-2 mt-1">
      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[10px] text-neutral-500 disabled:opacity-30">↑</button>
      <button onClick={() => move(i, 1)} disabled={i === len - 1} className="text-[10px] text-neutral-500 disabled:opacity-30">↓</button>
      <button onClick={() => remove(i)} className="text-[10px] text-red-500 ml-auto">Remover</button>
    </div>
  );
}

function MultiStepFormEditor({ block, upd, index }) {
  const steps = block.steps || [];
  const { set, remove, move } = useListOps(steps, "steps", upd);
  const addStep = () => upd({ steps: [...steps, { title: "", next_label: "Continuar", fields: [] }] });
  return (
    <div className="space-y-3" data-testid={`lpblock-multistep-${index}`}>
      <div className="grid grid-cols-2 gap-2">
        <input value={block.form_id || ""} onChange={(e) => upd({ form_id: e.target.value })} placeholder="ID do formulário (ex: form-principal)" className={`${IN} font-mono`} />
        <input value={block.submit_label || ""} onChange={(e) => upd({ submit_label: e.target.value })} placeholder="Texto do botão final" className={IN} />
      </div>
      <input value={block.eyebrow || ""} onChange={(e) => upd({ eyebrow: e.target.value })} placeholder="Eyebrow (linha pequena acima do título)" className={IN} />
      <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })} placeholder="Título" className={`${IN} font-display text-base`} />
      <textarea rows={2} value={block.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })} placeholder="Subtítulo" className={TA} />
      <input value={block.microcopy || ""} onChange={(e) => upd({ microcopy: e.target.value })} placeholder="Microcopy (frase pequena em itálico)" className={IN} />
      <div className="grid grid-cols-3 gap-2">
        <input value={block.whatsapp_phone || ""} onChange={(e) => upd({ whatsapp_phone: e.target.value })} placeholder="WhatsApp (5562...)" className={`${IN} font-mono`} />
        <input value={block.whatsapp_label || ""} onChange={(e) => upd({ whatsapp_label: e.target.value })} placeholder="Label do botão WhatsApp" className={IN} />
        <input value={block.whatsapp_message || ""} onChange={(e) => upd({ whatsapp_message: e.target.value })} placeholder="Mensagem inicial" className={IN} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={block.thank_you_title || ""} onChange={(e) => upd({ thank_you_title: e.target.value })} placeholder="Título do agradecimento" className={IN} />
        <input value={block.thank_you_body || ""} onChange={(e) => upd({ thank_you_body: e.target.value })} placeholder="Texto do agradecimento" className={IN} />
      </div>
      <textarea rows={2} value={block.lgpd_text || ""} onChange={(e) => upd({ lgpd_text: e.target.value })} placeholder="Texto do checkbox LGPD (vazio = sem checkbox)" className={TA} />

      <div className="border-t border-black/10 pt-3">
        <div className="text-xs uppercase font-semibold text-neutral-500 mb-2">Etapas ({steps.length})</div>
        {steps.map((st, i) => (
          <StepEditor key={i} step={st} i={i} len={steps.length} set={set} remove={remove} move={move} />
        ))}
        <button onClick={addStep} className={ADD_BTN}><Plus className="w-3 h-3 inline mr-1" /> Nova etapa</button>
      </div>
    </div>
  );
}

function StepEditor({ step, i, len, set, remove, move }) {
  const fields = step.fields || [];
  const setF = (k, patch) => set(i, { fields: fields.map((f, n) => (n === k ? { ...f, ...patch } : f)) });
  const remF = (k) => set(i, { fields: fields.filter((_, n) => n !== k) });
  const addF = () => set(i, { fields: [...fields, { name: `campo_${fields.length + 1}`, label: "", type: "text", required: false }] });
  return (
    <div className="border border-black/10 p-2 rounded mb-2 bg-neutral-50">
      <div className="grid grid-cols-2 gap-2">
        <input value={step.title || ""} onChange={(e) => set(i, { title: e.target.value })} placeholder={`Título da etapa ${i + 1}`} className={IN} />
        <input value={step.next_label || ""} onChange={(e) => set(i, { next_label: e.target.value })} placeholder="Texto do botão (ex: Continuar)" className={IN} />
      </div>
      <div className="mt-2 space-y-1.5">
        {fields.map((f, k) => (
          <div key={k} className="grid grid-cols-12 gap-1.5 items-center">
            <input value={f.name || ""} onChange={(e) => setF(k, { name: e.target.value })} placeholder="nome_interno" className="col-span-3 border border-black/15 px-2 py-1 rounded text-[11px] font-mono" />
            <input value={f.label || ""} onChange={(e) => setF(k, { label: e.target.value })} placeholder="Label visível" className="col-span-4 border border-black/15 px-2 py-1 rounded text-[11px]" />
            <select value={f.type || "text"} onChange={(e) => setF(k, { type: e.target.value })} className="col-span-2 border border-black/15 px-1 py-1 rounded text-[11px]">
              <option value="text">Texto</option><option value="email">Email</option><option value="tel">Telefone</option>
              <option value="textarea">Área</option><option value="select">Seleção</option>
            </select>
            <label className="col-span-2 text-[10px] flex items-center gap-1">
              <input type="checkbox" checked={!!f.required} onChange={(e) => setF(k, { required: e.target.checked })} /> obrig.
            </label>
            <button onClick={() => remF(k)} className="col-span-1 text-[11px] text-red-500">×</button>
            {f.type === "select" && (
              <input value={(f.options || []).join(", ")} onChange={(e) => setF(k, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                placeholder="Opções separadas por vírgula" className="col-span-12 border border-black/15 px-2 py-1 rounded text-[11px]" />
            )}
          </div>
        ))}
        <button onClick={addF} className="text-[11px] text-accent-orange font-semibold">+ campo</button>
      </div>
      <RowTools i={i} len={len} move={move} remove={remove} />
    </div>
  );
}

function LogosBandEditor({ block, upd, index }) {
  const logos = block.logos || [];
  const { set, remove, move } = useListOps(logos, "logos", upd);
  const [clients, setClients] = useState(null);
  const picked = block.client_names || [];
  useEffect(() => {
    api.get("/admin/collection/clients")
      .then(({ data }) => setClients((data || []).filter((c) => !c.is_deleted)))
      .catch(() => setClients([]));
  }, []);
  const toggle = (name) => {
    upd({ client_names: picked.includes(name) ? picked.filter((n) => n !== name) : [...picked, name] });
  };
  const scale = Number(block.logo_scale || 72);

  return (
    <div className="space-y-3" data-testid={`lpblock-logos-${index}`}>
      <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })} placeholder="Título da faixa" className={`${IN} font-display text-base`} />
      <textarea rows={2} value={block.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })} placeholder="Subtítulo" className={TA} />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Logos por linha (desktop)</label>
          <select value={block.columns || 6} onChange={(e) => upd({ columns: Number(e.target.value) })} className={IN}>
            {[3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 flex justify-between">
            Tamanho do logo <span className="font-mono text-black">{scale}%</span>
          </label>
          <input type="range" min={30} max={95} step={2} value={scale}
            onChange={(e) => upd({ logo_scale: Number(e.target.value) })}
            data-testid={`lpblock-logos-scale-${index}`} className="w-full" />
        </div>
      </div>

      {/* Importar marcas do site */}
      <div className="border border-black/10 rounded p-2 bg-neutral-50">
        <label className="text-sm flex items-center gap-2" data-testid={`lpblock-logos-use-site-${index}`}>
          <input type="checkbox" checked={!!block.use_site_clients}
            onChange={(e) => upd({ use_site_clients: e.target.checked })} />
          <span>Importar as <strong>marcas de clientes do site</strong> (mesma vitrine da home)</span>
        </label>
        {block.use_site_clients && (
          <div className="mt-2">
            <p className="text-[10px] text-neutral-500 mb-2">
              {picked.length
                ? `${picked.length} marca(s) selecionada(s) — apenas elas aparecem, nesta ordem.`
                : "Nenhuma selecionada = exibe TODAS as marcas cadastradas em Clientes."}
            </p>
            {clients === null ? (
              <div className="text-xs text-neutral-500 flex items-center gap-2 py-2"><Loader2 className="w-3 h-3 animate-spin" /> Carregando marcas…</div>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-auto">
                {clients.map((c) => {
                  const on = picked.includes(c.name);
                  return (
                    <button key={c.name} onClick={() => toggle(c.name)}
                      data-testid={`lpblock-client-pick-${index}`}
                      className={`text-[11px] px-2.5 py-1.5 rounded-full border flex items-center gap-1.5 ${on ? "bg-black text-white border-black" : "border-black/15 hover:border-black/40"}`}>
                      {c.logo && <img src={resolveSrc(c.logo)} alt="" className="h-4 w-auto object-contain" />}
                      {c.name}
                    </button>
                  );
                })}
                {!clients.length && <span className="text-xs text-neutral-500">Nenhum cliente cadastrado ainda.</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {!block.use_site_clients && (
        <>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Logos manuais desta LP</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {logos.map((l, i) => (
              <div key={i} className="border border-black/10 p-2 rounded space-y-1">
                {l.src && <img src={resolveSrc(l.src)} alt="" className="w-full h-12 object-contain" />}
                <input value={l.name || ""} onChange={(e) => set(i, { name: e.target.value })} placeholder="Nome do cliente" className="w-full border border-black/15 px-1.5 py-1 rounded text-[11px]" />
                <input value={l.src || ""} onChange={(e) => set(i, { src: e.target.value })} placeholder="URL do logo" className="w-full border border-black/15 px-1.5 py-1 rounded text-[10px] font-mono" />
                <DropZone multiple={false} accept={{ "image/*": [] }} onUploaded={(f) => set(i, { src: f.url })} />
                <RowTools i={i} len={logos.length} move={move} remove={remove} />
              </div>
            ))}
          </div>
          <button onClick={() => upd({ logos: [...logos, { name: "", src: "" }] })} className={ADD_BTN}>
            <Plus className="w-3 h-3 inline mr-1" /> Adicionar logo manual
          </button>
        </>
      )}
    </div>
  );
}

function FaqEditor({ block, upd, index }) {
  const items = block.items || [];
  const { set, remove, move } = useListOps(items, "items", upd);
  return (
    <div className="space-y-2" data-testid={`lpblock-faq-${index}`}>
      <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })} placeholder="Título da seção" className={`${IN} font-display text-base`} />
      {items.map((it, i) => (
        <div key={i} className="border border-black/10 p-2 rounded">
          <input value={it.q || ""} onChange={(e) => set(i, { q: e.target.value })} placeholder="Pergunta" className={`${IN} font-semibold`} />
          <textarea rows={3} value={it.a || ""} onChange={(e) => set(i, { a: e.target.value })} placeholder="Resposta" className={`${TA} mt-1.5`} />
          <RowTools i={i} len={items.length} move={move} remove={remove} />
        </div>
      ))}
      <button onClick={() => upd({ items: [...items, { q: "", a: "" }] })} className={ADD_BTN}>
        <Plus className="w-3 h-3 inline mr-1" /> Nova pergunta
      </button>
      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-black/10">
        <input value={block.cta_label || ""} onChange={(e) => upd({ cta_label: e.target.value })} placeholder="CTA — texto (opcional)" className={IN} />
        <input value={block.cta_link || ""} onChange={(e) => upd({ cta_link: e.target.value })} placeholder="#form-principal" className={`${IN} font-mono`} />
      </div>
    </div>
  );
}

function ProcessTimelineEditor({ block, upd, index }) {
  const steps = block.steps || [];
  const { set, remove, move } = useListOps(steps, "steps", upd);
  return (
    <div className="space-y-2" data-testid={`lpblock-process-${index}`}>
      <input value={block.title || ""} onChange={(e) => upd({ title: e.target.value })} placeholder="Título" className={`${IN} font-display text-base`} />
      <textarea rows={2} value={block.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })} placeholder="Subtítulo" className={TA} />
      {steps.map((s, i) => (
        <div key={i} className="border border-black/10 p-2 rounded">
          <input value={s.label || ""} onChange={(e) => set(i, { label: e.target.value })} placeholder={`Etapa ${i + 1} — rótulo curto`} className={`${IN} font-semibold`} />
          <textarea rows={2} value={s.description || ""} onChange={(e) => set(i, { description: e.target.value })} placeholder="Descrição exibida ao selecionar" className={`${TA} mt-1.5`} />
          <RowTools i={i} len={steps.length} move={move} remove={remove} />
        </div>
      ))}
      <button onClick={() => upd({ steps: [...steps, { label: "", description: "" }] })} className={ADD_BTN}>
        <Plus className="w-3 h-3 inline mr-1" /> Nova etapa
      </button>
      <input value={block.footer || ""} onChange={(e) => upd({ footer: e.target.value })} placeholder="Frase de fechamento (itálico)" className={IN} />
    </div>
  );
}

function ServicesGroupedEditor({ block, upd, index }) {
  const groups = block.groups || [];
  const { set, remove, move } = useListOps(groups, "groups", upd);
  return (
    <div className="space-y-2" data-testid={`lpblock-services-${index}`}>
      <textarea rows={2} value={block.title || ""} onChange={(e) => upd({ title: e.target.value })} placeholder="Título da seção" className={`${TA} font-display text-base`} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {groups.map((g, i) => (
          <div key={i} className="border border-black/10 p-2 rounded">
            <input value={g.title || ""} onChange={(e) => set(i, { title: e.target.value })} placeholder="Nome do grupo" className={`${IN} font-semibold`} />
            <textarea rows={5} value={(g.items || []).join("\n")} onChange={(e) => set(i, { items: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              placeholder="Um serviço por linha" className={`${TA} mt-1.5 text-xs`} />
            <RowTools i={i} len={groups.length} move={move} remove={remove} />
          </div>
        ))}
      </div>
      <button onClick={() => upd({ groups: [...groups, { title: "", items: [] }] })} className={ADD_BTN}>
        <Plus className="w-3 h-3 inline mr-1" /> Novo grupo
      </button>
      <input value={block.footer || ""} onChange={(e) => upd({ footer: e.target.value })} placeholder="Frase de fechamento (itálico)" className={IN} />
      <div className="grid grid-cols-2 gap-2">
        <input value={block.cta_label || ""} onChange={(e) => upd({ cta_label: e.target.value })} placeholder="CTA — texto" className={IN} />
        <input value={block.cta_link || ""} onChange={(e) => upd({ cta_link: e.target.value })} placeholder="#form-principal" className={`${IN} font-mono`} />
      </div>
    </div>
  );
}

/** CaseShowcaseEditor — seletor dos cases REAIS do site.
 *  Ao marcar um case, a LP importa automaticamente thumb (cover), cliente
 *  (incorporadora + cidade), nome, categorias, link e resumo de
 *  situação/solução direto da ficha do case. Zero redigitação. */
function CaseShowcaseEditor({ block, upd, index }) {
  const [cases, setCases] = useState(null);
  const selected = block.case_slugs || [];
  useEffect(() => {
    api.get("/admin/collection/cases")
      .then(({ data }) => setCases((data || []).filter((c) => !c.is_deleted)))
      .catch(() => setCases([]));
  }, []);
  const toggle = (slug) => {
    upd({ case_slugs: selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug] });
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= selected.length) return;
    const copy = [...selected];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    upd({ case_slugs: copy });
  };
  const byslug = Object.fromEntries((cases || []).map((c) => [c.slug, c]));

  return (
    <div className="space-y-3" data-testid={`lpblock-caseshowcase-${index}`}>
      <input value={block.eyebrow || ""} onChange={(e) => upd({ eyebrow: e.target.value })} placeholder="Eyebrow" className={IN} />
      <textarea rows={2} value={block.title || ""} onChange={(e) => upd({ title: e.target.value })} placeholder="Título da seção" className={`${TA} font-display text-base`} />
      <div className="grid grid-cols-2 gap-2">
        <input value={block.cta_label || ""} onChange={(e) => upd({ cta_label: e.target.value })} placeholder="Texto do botão do case (ex: Ver case)" className={IN} />
        <input value={block.cta_secondary_label || ""} onChange={(e) => upd({ cta_secondary_label: e.target.value })} placeholder="Botão secundário (ex: Quero um projeto assim)" className={IN} />
      </div>
      <input value={block.cta_secondary_link || ""} onChange={(e) => upd({ cta_secondary_link: e.target.value })} placeholder="#form-principal" className={`${IN} font-mono`} />

      {selected.length > 0 && (
        <div className="border border-black/10 rounded p-2 bg-neutral-50">
          <div className="text-[10px] uppercase font-semibold tracking-widest text-neutral-500 mb-2">Ordem na LP ({selected.length})</div>
          <div className="space-y-1">
            {selected.map((s, i) => (
              <div key={s} className="flex items-center gap-2 bg-white border border-black/10 rounded px-2 py-1">
                {byslug[s]?.cover && <img src={resolveSrc(byslug[s].cover)} alt="" className="w-12 h-8 object-cover rounded" />}
                <span className="text-xs flex-1 truncate">{byslug[s]?.title || s}</span>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[11px] text-neutral-500 disabled:opacity-30">↑</button>
                <button onClick={() => move(i, 1)} disabled={i === selected.length - 1} className="text-[11px] text-neutral-500 disabled:opacity-30">↓</button>
                <button onClick={() => toggle(s)} className="text-[11px] text-red-500">×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase font-semibold tracking-widest text-neutral-500 mb-2">
          Escolha os cases do site — thumb, cliente, categorias e link são importados automaticamente
        </div>
        {cases === null ? (
          <div className="text-xs text-neutral-500 flex items-center gap-2 py-3"><Loader2 className="w-3 h-3 animate-spin" /> Carregando cases…</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-72 overflow-auto p-1">
            {cases.map((c) => {
              const on = selected.includes(c.slug);
              return (
                <button key={c.slug} onClick={() => toggle(c.slug)}
                  data-testid={`lpblock-case-pick-${c.slug}`}
                  className={`text-left border rounded overflow-hidden transition-all ${on ? "border-accent-orange ring-2 ring-accent-orange/30" : "border-black/10 hover:border-black/30"}`}>
                  <div className="aspect-video bg-neutral-200 overflow-hidden">
                    {c.cover && <img src={resolveSrc(c.cover)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-1.5">
                    <div className="text-[11px] font-semibold truncate">{c.title}</div>
                    <div className="text-[10px] text-neutral-500 truncate">{c.developer}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {block.cases?.length > 0 && selected.length === 0 && (
          <p className="text-[10px] text-amber-600 mt-2">
            Esta LP ainda usa {block.cases.length} case(s) digitado(s) manualmente. Selecione os cases reais acima para substituir por dados e thumbs importados automaticamente.
          </p>
        )}
      </div>
    </div>
  );
}

/* -------------------- Main -------------------- */
export default function LPBlocksEditor({ blocks = [], onChange, testidPrefix = "lp-blocks" }) {
  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oi = blocks.findIndex((b) => b.id === active.id);
    const ni = blocks.findIndex((b) => b.id === over.id);
    onChange(arrayMove(blocks, oi, ni));
  };
  const upd = (nb) => onChange(blocks.map((b) => (b.id === nb.id ? nb : b)));
  const rem = (id) => onChange(blocks.filter((b) => b.id !== id));

  const add = (type) => {
    const base = { id: newLPBlockId(), type, spacing_px: 48 };
    if (type === "hero")        Object.assign(base, { image: "", title: "", subtitle: "", cta_label: "", cta_link: "#form", height: "70vh", overlay_opacity: 0.4, text_color: "white", show_brand: true, brand_color: "white" });
    if (type === "richtext")    Object.assign(base, { html: "<p></p>", max_width: "prose", align: "left", bg: "white" });
    if (type === "image")       Object.assign(base, { src: "", alt: "", caption: "", max_width: "wide" });
    if (type === "gallery")     Object.assign(base, { images: [], columns: 3 });
    if (type === "video")       Object.assign(base, { url: "", caption: "" });
    if (type === "features")    Object.assign(base, { title: "", subtitle: "", columns: 3, items: [{ icon: "star", title: "Benefício", description: "Descrição" }] });
    if (type === "testimonial") Object.assign(base, { items: [] });
    if (type === "form")        Object.assign(base, {
      form_id: `form-${blocks.length + 1}`, title: "Fale com um especialista", subtitle: "",
      submit_label: "Enviar", thank_you: "Recebemos seu contato. Retornaremos em breve.",
      fields: [
        { name: "name", label: "Nome", type: "text", required: true, placeholder: "Seu nome" },
        { name: "email", label: "Email", type: "email", required: true, placeholder: "seu@email.com" },
        { name: "phone", label: "WhatsApp", type: "tel", required: false, placeholder: "(00) 00000-0000" },
        { name: "message", label: "Mensagem", type: "textarea", required: false, placeholder: "Como podemos ajudar?" },
      ],
    });
    if (type === "cta")         Object.assign(base, { title: "", subtitle: "", buttons: [] });
    if (type === "spacer")      Object.assign(base, { height_px: 60, bg: "white" });
    if (type === "divider")     Object.assign(base, { style: "line", color: "" });
    if (type === "html")        Object.assign(base, { html: "" });
    if (type === "stats")       Object.assign(base, { items: [], columns: 3 });
    if (type === "multi_step_form") Object.assign(base, {
      form_id: `form-${blocks.length + 1}`, title: "Vamos falar do seu projeto", subtitle: "",
      submit_label: "Enviar", thank_you_title: "Recebemos seu projeto.", thank_you_body: "Nossa equipe entra em contato em até 1 dia útil.",
      lgpd_text: "Autorizo o contato da Places e concordo com a política de privacidade.",
      steps: [
        { title: "Quem é você", next_label: "Continuar", fields: [
          { name: "name", label: "Nome", type: "text", required: true },
          { name: "email", label: "Email", type: "email", required: true },
        ] },
        { title: "Seu projeto", next_label: "Enviar", fields: [
          { name: "phone", label: "WhatsApp", type: "tel", required: true },
          { name: "message", label: "Conte sobre o projeto", type: "textarea", required: false },
        ] },
      ],
    });
    if (type === "logos_band")  Object.assign(base, { title: "", subtitle: "", logos: [], use_site_clients: true, client_names: [], columns: 6, logo_scale: 72 });
    if (type === "faq")         Object.assign(base, { title: "Perguntas frequentes", items: [{ q: "", a: "" }] });
    if (type === "process_timeline") Object.assign(base, { title: "", subtitle: "", steps: [{ label: "Diagnóstico", description: "" }] });
    if (type === "services_grouped") Object.assign(base, { title: "", groups: [{ title: "", items: [] }] });
    if (type === "case_showcase")    Object.assign(base, { eyebrow: "Cases", title: "", case_slugs: [], cta_label: "Ver case", cta_secondary_label: "Quero um projeto assim", cta_secondary_link: "#form-principal" });
    onChange([...blocks, base]);
  };

  const BTN = "text-xs bg-accent-yellow text-black px-3 py-2 rounded-full font-semibold hover:bg-black hover:text-white transition-colors";

  return (
    <div className="space-y-4" data-testid={testidPrefix}>
      <div className="flex flex-wrap gap-2">
        {Object.entries(TYPE_META).map(([key, m]) => {
          const Icon = m.Icon;
          return (
            <button key={key} onClick={() => add(key)} data-testid={`${testidPrefix}-add-${key}`} className={BTN}>
              <Icon className="w-3 h-3 inline mr-1" /> {m.label.split(" (")[0]}
            </button>
          );
        })}
      </div>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {blocks.map((b, i) => <SortableItem key={b.id} block={b} index={i} onChange={upd} onRemove={rem} />)}
            {!blocks.length && (
              <div className="border-2 border-dashed border-black/15 rounded p-8 text-center text-sm text-neutral-500">
                Nenhum bloco ainda. Adicione um Hero para começar → Formulário → CTA.
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
